<template>
  <div id="main-content">
    <div class="content-page-banner">
      <div class="banner-content">
        <h1>{{ $route.meta.title }}</h1>
        <div class="underline"></div>
      </div>
    </div>
    <RecordsView
      :apiStatus="apiStatus"
      :recordsData="recordsData"
      :referenceData="referenceData"
      :isExternalLink="true"
      :recordLink="recordLink"
      :includeFilter="true"
      :filterData="filterData"
      :includeRecentHeader="false"
      :includeRecordHeader="false"
      @updatedFilterData="updateFilterData"
    >
      <template v-slot:pagination>
        <Pagination isSPA @toPageNumber="paginationNavigation" :endPage="paginationParams.lastPageNumber" :currentPageFromParent="paginationParams.pageNum" />
      </template>
    </RecordsView>
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import orderBy from 'lodash.orderby';
import { VUEX_GENERAL } from '@/constants/VuexConstants';
import { ERROR_404_PAGE_NOT_FOUND } from '@/router/routeNames';
import { getAccessLevel, getCoveredAs, sortMembersByRelationship, convertEventDateToDateObj, initiatePdfFileDownload, formatEventDate } from '@/utils';
import { setQueryString } from '@/utils/UrlUtils';
import { NonDependantFlexCodes, FilterTypes } from '@/enums';
import Pagination from '@general/js/components/Pagination.vue';
import RecordsView from '@/components/RecordsView.vue';
import MyDocumentsServices from '@/services/MyCover/MyDocuments';
import MemberDetailsServices from '@/services/MemberDetails';

const FILTER_HEADINGS = {
  DOCUMENT: 'Document',
  MEMBER_NAME: 'Member name',
  CREATED_DATE_FROM: 'Created date from',
  CREATED_DATE_TO: 'Created date to',
  CLAIM_OR_QUOTE_ID: 'Claim/QuoteID',
};

const FILTER_PLACEHOLDERS = {
  DOCUMENT: 'Select documents',
  MEMBER_NAME: 'Select member name',
  FORM_DATE: 'YYYY / MM / DD',
  CLAIM_OR_QUOTE_ID: 'e.g. 12345 / 123456789',
};

const MY_DOCUMENTS_HEADINGS = {
  DOCUMENT: 'Document',
  MEMBER_NAME: 'Member name',
  CREATED_DATE: 'Created date',
  CLAIM_OR_QUOTE_ID: 'Claim/QuoteID',
};

const FILTER_OPTIONS_INDEX = {
  DOCUMENT: 0,
  MEMBER_NAME: 1,
  CREATED_DATE_FROM: 2,
  CREATED_DATE_TO: 3,
  CLAIM_OR_QUOTE_ID: 4,
};

export default {
  name: 'MyDocuments',

  components: {
    RecordsView,
    Pagination,
  },

  data() {
    return {
      apiStatus: {
        refData: {
          fail: false,
          loading: false,
        },
        recordData: {
          fail: false,
          loading: false,
        },
        recordDocument: {
          fail: false,
          loading: false,
        },
      },
      referenceData: {
        informationText: null,
      },
      recordsData: {
        headings: Object.values(MY_DOCUMENTS_HEADINGS),
        data: [],
        noRecordsText: 'You currently have no documents available',
      },
      filterData: {
        filterName: 'documents',
        filterFunction: this.applyFilters,
        filterOptions: [],
        filterDropdownData: [],
        filterNoRecordsText: 'We cannot find any records that match your search. Please adjust your search criteria and try again.',
      },
      filterResult: {
        selectedDocument: '',
        selectedMemberName: '',
        createdDateFrom: '',
        createdDateTo: '',
        claimID: '',
      },
      accessLevel: '',
      coveredAs: '',
      fullName: '',
      nonDependantList: Object.values(NonDependantFlexCodes),
      recordLink: {
        recordLinkFunction: this.fetchDocumentFile,
        recordLinkParams: 'documentId',
      },
      documents: [],
      memberNames: [],
      documentSummaryData: [],
      filteredDocumentData: [],
      paginationParams: {
        pageNum: 1,
        pageSize: 10,
        totalNum: 0,
        lastPageNumber: 1,
        itemName: 'documents',
      },
      urlDataToCleanup: [],
    };
  },

  beforeMount() {
    this.getMemberDetails();
  },

  mounted() {
    this.getReferenceData();
    this.fetchDocumentSummary();
  },

  beforeUnmount() {
    // Explicitly unload URL for optimal performance and memory usage
    this.urlDataToCleanup.forEach((url) => {
      URL.revokeObjectURL(url);
    });
  },

  computed: {
    ...mapGetters({
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      currentMemberDetails: VUEX_GENERAL.GETTERS.getCurrentMemberDetails,
      memberships: VUEX_GENERAL.GETTERS.memberships,
      members: VUEX_GENERAL.GETTERS.getMembers,
    }),

    errorPageRouter() {
      return this.$router.resolve({ name: ERROR_404_PAGE_NOT_FOUND });
    },
  },

  watch: {
    '$route.query.pageNum': {
      handler(newNum, oldNum) {
        if (newNum !== oldNum) {
          this.paginationNavigation(newNum);
        }
      },
    },
  },

  methods: {
    ...mapMutations([VUEX_GENERAL.MUTATIONS.updateMembers]),

    getMemberDetails() {
      this.accessLevel = getAccessLevel(this.memberNumber, this.memberships).toLowerCase();
      this.coveredAs = getCoveredAs(this.memberNumber, this.memberships).toUpperCase();
      this.fullName = `${this.currentMemberDetails.firstName} ${this.currentMemberDetails.lastName}`;
    },

    async getReferenceData() {
      this.apiStatus.refData.loading = true;
      this.apiStatus.refData.fail = false;

      try {
        const refData = await MyDocumentsServices.getReferenceData();
        if (refData?.myDocumentsInformationText?.informationText) {
          this.referenceData.informationText = refData.myDocumentsInformationText.informationText.tokenSwap();
        }
      } catch (error) {
        this.apiStatus.refData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.refData.loading = false;
      }
    },

    async fetchDocumentSummary() {
      this.apiStatus.recordData.loading = true;
      this.apiStatus.recordData.fail = false;

      try {
        const documentsSummary = await MyDocumentsServices.getDocumentSummary(this.memberNumber);

        this.documentSummaryData = this.formatDocumentRecordData(documentsSummary);
        // Only display record data that is within page size
        this.recordsData.data = this.documentSummaryData.slice(0, this.paginationParams.pageSize);

        // Update total number of record data for pagination
        this.paginationParams.totalNum = this.documentSummaryData.length;
        // Update last page number for pagination
        this.paginationParams.lastPageNumber = Math.ceil(this.paginationParams.totalNum / this.paginationParams.pageSize);

        // Get members in policy if members do not yet exist
        if (!this.members) {
          const allMembers = await MemberDetailsServices.getMembers(this.memberNumber);
          this.updateMembers(allMembers);
        }
        this.mapMemberNameToDocSummary(this.members, this.documentSummaryData);
        this.addFilterOptions(this.members);
        this.updateFilterOptions();
      } catch (error) {
        this.apiStatus.recordData.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.recordData.loading = false;
      }
    },

    async fetchDocumentFile(documentId) {
      this.apiStatus.recordDocument.fail = false;
      this.apiStatus.recordDocument.loading = true;

      const documentBeingAccessed = this.recordsData?.data.find((record) => {
        return record.documentId === documentId;
      });

      const accessingPersonCode = documentBeingAccessed.personCode;
      const docName = documentBeingAccessed.Document;

      try {
        // personCode means accessingPersonCode
        const fileData = await MyDocumentsServices.getDocumentFile(this.memberNumber, accessingPersonCode, documentId);
        initiatePdfFileDownload(fileData, docName, this.urlDataToCleanup);
      } catch (error) {
        this.apiStatus.recordDocument.fail = true;

        window.open(this.errorPageRouter.href, '_blank'); // Open error page in a new tab
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.recordDocument.loading = false;
      }
    },

    formatDocumentRecordData(unformattedDocumentData) {
      let formattedDocumentData = [];

      if (unformattedDocumentData) {
        formattedDocumentData = unformattedDocumentData.map((documentData) => {
          return {
            [MY_DOCUMENTS_HEADINGS.DOCUMENT]: documentData.formName,
            [MY_DOCUMENTS_HEADINGS.MEMBER_NAME]: documentData.fullName ? documentData.fullName : `${documentData.firstName} ${documentData.lastName}`,
            [MY_DOCUMENTS_HEADINGS.CREATED_DATE]: documentData.eventDateTime ? formatEventDate(documentData.eventDateTime) : null,
            [MY_DOCUMENTS_HEADINGS.CLAIM_OR_QUOTE_ID]: documentData.claimId,
            personCode: documentData.personCode, // Include personCode as hidden attribute
            documentId: documentData.documentId, // Document Id for document download
          };
        });

        // Order document summary data by event time descending
        formattedDocumentData = orderBy(formattedDocumentData, [MY_DOCUMENTS_HEADINGS.CREATED_DATE], ['desc']);
      }

      return formattedDocumentData;
    },

    mapMemberNameToDocSummary(allMembers, documentSummaryData) {
      for (let i = 0; i < documentSummaryData.length; i++) {
        const matchedMember = allMembers?.find((member) => {
          return member.personCode === documentSummaryData[i].personCode;
        });
        if (matchedMember && documentSummaryData[i][MY_DOCUMENTS_HEADINGS.MEMBER_NAME] !== `${matchedMember.firstName} ${matchedMember.lastName}`) {
          documentSummaryData[i][MY_DOCUMENTS_HEADINGS.MEMBER_NAME] = `${matchedMember.firstName} ${matchedMember.lastName}`;
        }
      }
    },

    addFilterOptions(allMemberDetails) {
      this.documents = this.documentSummaryData
        .map((dataObj) => {
          return dataObj[MY_DOCUMENTS_HEADINGS.DOCUMENT];
        })
        .filter((value, index, selfArr) => {
          return selfArr.indexOf(value) === index;
        });

      // Get all unique members from fetch document summary since DOA (Access level of logged member) is already applied in EAI level
      this.memberNames = this.documentSummaryData.reduce((dataArr, dataObj) => {
        const memberExist = dataArr.some((obj) => {
          return obj.value === dataObj[MY_DOCUMENTS_HEADINGS.MEMBER_NAME];
        });

        const memberDetailsMember = allMemberDetails.find((member) => {
          return member.personCode === dataObj.personCode;
        });

        // Add to member list if member is not yet added
        if (!memberExist && dataObj[MY_DOCUMENTS_HEADINGS.MEMBER_NAME]) {
          dataArr.push({
            name: dataObj[MY_DOCUMENTS_HEADINGS.MEMBER_NAME],
            relationship: memberDetailsMember ? memberDetailsMember.coveredOnMembership : '',
            value: dataObj[MY_DOCUMENTS_HEADINGS.MEMBER_NAME],
          });
        }
        return dataArr;
      }, []);

      this.memberNames = sortMembersByRelationship(this.memberNames);
    },

    updateFilterOptions() {
      this.filterData.filterOptions = [
        {
          filterHeading: FILTER_HEADINGS.DOCUMENT,
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: FILTER_PLACEHOLDERS.DOCUMENT,
          filterDropdownData: this.documents,
          filterResult: this.filterResult.selectedDocument,
        },
        {
          filterHeading: FILTER_HEADINGS.MEMBER_NAME,
          filterType: FilterTypes.DROPDOWN_INPUT,
          filterPlaceholder: FILTER_PLACEHOLDERS.MEMBER_NAME,
          filterDropdownData: this.memberNames,
          filterResult: this.filterResult.selectedMemberName,
        },
        {
          filterHeading: FILTER_HEADINGS.CREATED_DATE_FROM,
          filterType: FilterTypes.EVENT_DATE_INPUT,
          filterPlaceholder: FILTER_PLACEHOLDERS.FORM_DATE,
          filterResult: this.filterResult.createdDateFrom,
        },
        {
          filterHeading: FILTER_HEADINGS.CREATED_DATE_TO,
          filterType: FilterTypes.EVENT_DATE_INPUT,
          filterPlaceholder: FILTER_PLACEHOLDERS.FORM_DATE,
          filterResult: this.filterResult.createdDateTo,
        },
        {
          filterHeading: FILTER_HEADINGS.CLAIM_OR_QUOTE_ID,
          filterType: FilterTypes.TEXT_INPUT,
          filterPlaceholder: FILTER_PLACEHOLDERS.CLAIM_OR_QUOTE_ID,
          filterResult: this.filterResult.claimID,
        },
      ];
    },

    updateFilterData(filterData) {
      const filterOptions = filterData.filterOptions;

      // Update filterResult values when chaging filter values
      this.filterResult.selectedDocument = filterOptions[FILTER_OPTIONS_INDEX.DOCUMENT].filterResult;
      this.filterResult.selectedMemberName = filterOptions[FILTER_OPTIONS_INDEX.MEMBER_NAME].filterResult;
      this.filterResult.createdDateFrom = filterOptions[FILTER_OPTIONS_INDEX.CREATED_DATE_FROM].filterResult;
      this.filterResult.createdDateTo = filterOptions[FILTER_OPTIONS_INDEX.CREATED_DATE_TO].filterResult;
      this.filterResult.claimID = filterOptions[FILTER_OPTIONS_INDEX.CLAIM_OR_QUOTE_ID].filterResult;
    },

    applyFilters() {
      this.paginationParams.pageNum = 1;
      setQueryString('pageNum', 1); // Always move to first page when filter is applied

      // Apply the non-date filters
      this.filteredDocumentData = this.documentSummaryData.filter((documentRow) => {
        return (
          this.checkIfMatchOrDefault(documentRow[MY_DOCUMENTS_HEADINGS.DOCUMENT], this.filterResult.selectedDocument) &&
          this.checkIfMatchOrDefault(documentRow[MY_DOCUMENTS_HEADINGS.MEMBER_NAME], this.filterResult.selectedMemberName) &&
          this.checkIfMatchOrDefault(documentRow[MY_DOCUMENTS_HEADINGS.CLAIM_OR_QUOTE_ID], this.filterResult.claimID)
        );
      });

      // Apply date filters
      this.filteredDocumentData = this.filteredDocumentData.filter((documentRow) => {
        if (this.filterResult.createdDateFrom === '' && this.filterResult.createdDateTo === '') {
          return true;
        }
        if (this.filterResult.createdDateFrom !== '' && this.filterResult.createdDateTo === '') {
          return convertEventDateToDateObj(documentRow[MY_DOCUMENTS_HEADINGS.CREATED_DATE]) >= new Date(this.filterResult.createdDateFrom);
        }
        if (this.filterResult.createdDateFrom === '' && this.filterResult.createdDateTo !== '') {
          return convertEventDateToDateObj(documentRow[MY_DOCUMENTS_HEADINGS.CREATED_DATE]) <= new Date(this.filterResult.createdDateTo);
        }
        return (
          convertEventDateToDateObj(documentRow[MY_DOCUMENTS_HEADINGS.CREATED_DATE]) >= new Date(this.filterResult.createdDateFrom) &&
          convertEventDateToDateObj(documentRow[MY_DOCUMENTS_HEADINGS.CREATED_DATE]) <= new Date(this.filterResult.createdDateTo)
        );
      });

      this.paginationParams.totalNum = this.filteredDocumentData.length;
      this.paginationParams.lastPageNumber = Math.ceil(this.paginationParams.totalNum / this.paginationParams.pageSize);

      this.recordsData.data = this.filteredDocumentData.slice(0, this.paginationParams.pageSize);
    },

    paginationNavigation(pageNum) {
      this.paginationParams.pageNum = Number(pageNum);
      this.$route.query.pageNum = String(pageNum);

      if (this.filteredDocumentData.length > 0) {
        this.recordsData.data = this.filteredDocumentData.slice(
          (this.paginationParams.pageNum - 1) * this.paginationParams.pageSize,
          this.paginationParams.pageNum * this.paginationParams.pageSize,
        );
      } else {
        this.recordsData.data = this.documentSummaryData.slice(
          (this.paginationParams.pageNum - 1) * this.paginationParams.pageSize,
          this.paginationParams.pageNum * this.paginationParams.pageSize,
        );
      }
    },

    checkIfMatchOrDefault(valueTobeChecked, filterValue) {
      // if the filter is empty or all
      if (filterValue.startsWith('Select ') || filterValue === '') {
        return true;
      }

      return valueTobeChecked === filterValue;
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.top-content) {
  #main-content {
    p:last-child {
      margin-bottom: 0;
    }
  }
}

:deep(#main-content-paragraph) {
  width: 100% !important;
}

.pagination-label {
  min-width: 180px;
  float: right;
  margin-bottom: spacer(3);

  @include mobile {
    position: relative;
  }
}
</style>
